/**
 * FormContext - Contexto global para o Formulário Interativo
 */

import React, { createContext, useReducer, useCallback, ReactNode } from 'react';
import { FormData, FormContextType, FormAction } from './types';
import { STEP_ORDER } from '../constants';

const FORM_SESSION_KEY = 'formulario_form_data';

const saveFormToSession = (data: FormData) => {
  try {
    sessionStorage.setItem(FORM_SESSION_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Erro ao salvar formulário em sessão:', error);
  }
};

const loadFormFromSession = (): Partial<FormData> | null => {
  try {
    const saved = sessionStorage.getItem(FORM_SESSION_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Erro ao carregar formulário de sessão:', error);
    return null;
  }
};

const clearFormFromSession = () => {
  try {
    sessionStorage.removeItem(FORM_SESSION_KEY);
  } catch (error) {
    console.error('Erro ao limpar formulário de sessão:', error);
  }
};

const initialFormData: FormData = {
  cityState: '',
  fullName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  nationality: '',
  rgDocument: '',
  cpfDocument: '',
  cpfRestrictions: '',
  avalist: '',
  cep: '',
  address: '',
  addressNumber: '',
  complement: '',
  neighborhood: '',
  residence: '',
  residenceTime: '',
  residenceProof: '',
  children: '',
  childrenCount: undefined,
  job: '',
  maritalStatus: '',
  mothersName: '',
  scholarLevel: '',
  income: '',
  creditCard: '',
  veiculo: '',
  instagram: '',
  referenceNameOne: '',
  referencePhoneOne: '',
  referenceNameTwo: '',
  referencePhoneTwo: '',
  referenceComercial: '',
  referenceComercialPhone: '',
  salesMotivation: '',
  ambassadorReason: '',
  xizinhaInterest: '',
  appLearning: '',
  technologyConfidence: '',
  contractAgreement: '',
  doubtsQuestion: '',
  disponivelEntrevistaOnline: '',
  disponivelTreinamentoVendas: '',
  ipAddress: '',
  profitGoal: '',
  userAgent: '',
  indicatedCode: '',
};

const formReducer = (state: FormData, action: FormAction): FormData => {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'SET_FIELDS':
      return {
        ...state,
        ...action.payload,
      };
    case 'RESET':
      return initialFormData;
    default:
      return state;
  }
};

export const FormContext = createContext<FormContextType | undefined>(undefined);

interface FormProviderProps {
  children: ReactNode;
  initialData?: Partial<FormData>;
  indicatedCode?: string;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children, initialData, indicatedCode }) => {
  const savedData = loadFormFromSession();
  const [formData, dispatch] = useReducer(formReducer, {
    ...initialFormData,
    ...savedData,
    ...initialData,
    indicatedCode: indicatedCode || initialData?.indicatedCode || savedData?.indicatedCode || '',
  });

  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const currentStepId = STEP_ORDER[currentStepIndex];

  const updateField = useCallback((field: keyof FormData, value: any) => {
    dispatch({ type: 'SET_FIELD', payload: { field, value } });
  }, []);

  React.useEffect(() => {
    saveFormToSession(formData);
  }, [formData]);

  const updateFields = useCallback((fields: Partial<FormData>) => {
    dispatch({ type: 'SET_FIELDS', payload: fields });
  }, []);

  const goToStep = useCallback((stepId: string) => {
    const stepIndex = STEP_ORDER.indexOf(stepId as any);
    if (stepIndex >= 0) {
      setCurrentStepIndex(stepIndex);
      setError(null);
    }
  }, []);

  const goToNextStep = useCallback((nextStepId?: string) => {
    if (nextStepId) {
      goToStep(nextStepId);
    } else if (currentStepIndex < STEP_ORDER.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setError(null);
    }
  }, [currentStepIndex, goToStep]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setError(null);
    }
  }, [currentStepIndex]);

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET' });
    setCurrentStepIndex(0);
    setError(null);
  }, []);

  const value: FormContextType = {
    data: formData,
    currentStepIndex,
    currentStepId,
    isLoading,
    error,
    updateField,
    updateFields,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    resetForm,
    setLoading: setIsLoading,
    setError: (msg: string | null) => setError(msg),
    clearFormSession: clearFormFromSession,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export const useFormContext = (): FormContextType => {
  const context = React.useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within FormProvider');
  }
  return context;
};

export { clearFormFromSession };
